/**
 * Apartsales — `/ai-search` route.
 *
 * The full "alternative" search flow Billions X asked for — completely
 * independent from the existing `/search` (Where/What/Price pills). Users
 * arrive here from the SmartSearchBar on home.
 *
 * Anatomy (top to bottom):
 *
 *   ┌───────────────────────────────────────┐
 *   │ ← Назад        AI-поиск               │  ← back chrome
 *   │                                       │
 *   │ ╔═══════════════════════════════════╗ │
 *   │ ║ ✨  Что ищешь? ...            ✕  ║ │  ← SmartSearchBar (interactive)
 *   │ ╚═══════════════════════════════════╝ │
 *   │                                       │
 *   │ [Сочи ×] [2-комн ×] [до 15М ×]        │  ← detected chips (removable)
 *   │                                       │
 *   │ 12 объектов · по релевантности ↑↓     │  ← count + sort
 *   ├───────────────────────────────────────┤
 *   │                                       │
 *   │       ┌────────┐  ┌────────┐          │
 *   │       │ Card 1 │  │ Card 2 │          │  ← grid via apiUnitToProperty
 *   │       └────────┘  └────────┘          │
 *   │       ...                             │
 *   └───────────────────────────────────────┘
 *
 * State machine is intentionally tiny:
 *   - `input`     : user's typed text (URL-synced via ?q=)
 *   - `removed`   : set of chip keys the user explicitly de-selected
 *   - `effective` : `parseSmartQuery(input)` minus `removed` fields
 *   - `apiQuery`  : `effective` mapped to API `SearchQuery` shape
 *
 * Parsing happens on every keystroke (regex, instant, sync). Results are
 * fetched via TanStack Query with debounce on the input → apiQuery key.
 *
 * Parser is regex-based MVP. LLM via Workers AI (`POST /v1/search/parse`)
 * ships in the next push and slots in here as a `useQuery` that falls back
 * to the regex output on failure — consumer shape is identical.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { Region, SearchQuery, Unit } from '@apartsales/shared';

import { SmartSearchBar } from '~/components/SmartSearchBar';
import { PropertyCardCompact } from '~/components/PropertyCardCompact';
import { ChevronLeftIcon } from '~/components/icons';
import { cn } from '~/lib/cn';
import { searchApi, ApiCallError } from '~/lib/api';
import { apiUnitToProperty } from '~/lib/adapters/unitToProperty';
import {
  chipsFromParsed,
  parseSmartQuery,
  type ChipKind,
  type ParsedChip,
  type ParsedQuery,
} from '~/lib/search/parseSmartQuery';

// ---------------------------------------------------------------------------
// Route setup
// ---------------------------------------------------------------------------

interface AiSearchSearch {
  q?: string;
}

export const Route = createFileRoute('/ai-search')({
  validateSearch: (s: Record<string, unknown>): AiSearchSearch => {
    return typeof s.q === 'string' ? { q: s.q } : {};
  },
  component: AiSearchScreen,
});

// ---------------------------------------------------------------------------
// API mapping
// ---------------------------------------------------------------------------

/**
 * Some "city" labels we detect (e.g. "Крым") are really regions in the API
 * enum. Translate them here so the API filter hits.
 */
const REGION_LABELS: Record<string, Region> = {
  Крым: 'crimea',
};

function toApiQuery(p: ParsedQuery): SearchQuery {
  const q: SearchQuery = { limit: 20 };
  if (p.city) {
    const asRegion = REGION_LABELS[p.city];
    if (asRegion) q.region = asRegion;
    else q.city = p.city;
  }
  if (p.rooms != null) q.rooms = p.rooms;
  if (p.type) q.type = p.type;
  if (p.price_min != null) q.price_min = p.price_min;
  if (p.price_max != null) q.price_max = p.price_max;
  // Features are not first-class filters in the API yet — fold them into FTS
  // so the russian text-search config can match against the project description.
  const ftsBits = [p.q, ...(p.features ?? [])].filter(Boolean) as string[];
  if (ftsBits.length > 0) q.q = ftsBits.join(' ');
  return q;
}

// ---------------------------------------------------------------------------
// Chip removal helper — strips one chip's contribution out of a ParsedQuery
// ---------------------------------------------------------------------------

function dropChip(p: ParsedQuery, kind: ChipKind, value: string): ParsedQuery {
  const next = { ...p };
  switch (kind) {
    case 'city':
      if (next.city === value) delete next.city;
      break;
    case 'rooms':
      if (next.rooms != null && String(next.rooms) === value) delete next.rooms;
      break;
    case 'type':
      if (next.type === value) delete next.type;
      break;
    case 'price':
      delete next.price_min;
      delete next.price_max;
      break;
    case 'feature':
      if (next.features) next.features = next.features.filter((f) => f !== value);
      if (next.features && next.features.length === 0) delete next.features;
      break;
    case 'q':
      if (next.q === value) delete next.q;
      break;
  }
  return next;
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

function AiSearchScreen() {
  const navigate = useNavigate({ from: '/ai-search' });
  const { q: qFromUrl } = useSearch({ from: '/ai-search' });

  // Input state — seeded from ?q=, then URL is kept in sync (replace, not push)
  const [input, setInput] = useState(qFromUrl ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Set of chip keys the user has explicitly removed ("kind:value")
  const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());

  // ----- URL sync on input changes (debounced via rAF batch) ---------------
  useEffect(() => {
    const id = window.setTimeout(() => {
      navigate({
        search: input.trim() ? { q: input.trim() } : {},
        replace: true,
      });
    }, 300);
    return () => window.clearTimeout(id);
  }, [input, navigate]);

  // ----- Parse + apply removals -------------------------------------------
  const parsed = useMemo(() => parseSmartQuery(input), [input]);

  const effective = useMemo(() => {
    let out = parsed;
    for (const key of removedKeys) {
      const sep = key.indexOf(':');
      if (sep < 0) continue;
      const kind = key.slice(0, sep) as ChipKind;
      const value = key.slice(sep + 1);
      out = dropChip(out, kind, value);
    }
    return out;
  }, [parsed, removedKeys]);

  const chips = useMemo(() => chipsFromParsed(effective), [effective]);

  const apiQuery = useMemo(() => toApiQuery(effective), [effective]);

  // ----- Fetch results -----------------------------------------------------
  // queryKey is the apiQuery — TanStack handles dedup + caching for free.
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['ai-search', apiQuery],
    queryFn: () => searchApi.search(apiQuery),
    // Only run after the user has typed something OR is browsing with no
    // query at all (in which case API returns recent units — acts as "all")
    enabled: true,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev, // keep last results while next batch loads
  });

  const isEmptyInput = input.trim().length === 0;

  // ----- Render -----------------------------------------------------------
  return (
    <section className="relative flex min-h-full flex-col bg-bg">
      {/* ─── Sticky header: back + bar + chips + meta ─── */}
      <div className="sticky top-0 z-10 -mx-4 bg-bg/95 px-4 pb-3 pt-3 backdrop-blur supports-[backdrop-filter]:bg-bg/75">
        <BackRow />
        <div className="mt-2">
          <SmartSearchBar
            ref={inputRef}
            interactive
            autoFocus={isEmptyInput}
            value={input}
            onChange={setInput}
            onSubmit={() => inputRef.current?.blur()}
          />
        </div>

        {chips.length > 0 && (
          <ChipRow
            chips={chips}
            onRemove={(c) =>
              setRemovedKeys((prev) => {
                const next = new Set(prev);
                next.add(`${c.kind}:${c.value}`);
                return next;
              })
            }
          />
        )}

        <ResultsMeta
          isLoading={isLoading || isFetching}
          total={data?.total}
          isError={isError}
        />
      </div>

      {/* ─── Body ─── */}
      <div className="flex-1 px-0 pb-12 pt-3">
        {isEmptyInput && !isLoading ? (
          <SuggestionsBlock onPick={(v) => setInput(v)} />
        ) : isError ? (
          <ErrorBlock message={messageFor(error)} />
        ) : (data?.items.length ?? 0) === 0 && !isLoading ? (
          <EmptyResultsBlock />
        ) : (
          <ResultsGrid items={data?.items ?? []} />
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function BackRow() {
  return (
    <div className="flex h-9 items-center">
      <Link
        to="/"
        className="flex h-9 w-9 -ml-1 items-center justify-center rounded-full text-label-2 transition-colors hover:bg-white/[0.06] active:scale-[0.94]"
        aria-label="Назад на главную"
      >
        <ChevronLeftIcon size={20} />
      </Link>
      <span className="ml-1 text-[14px] font-semibold uppercase tracking-[0.06em] text-label-2">
        AI-поиск
      </span>
    </div>
  );
}

interface ChipRowProps {
  chips: ParsedChip[];
  onRemove: (c: ParsedChip) => void;
}

function ChipRow({ chips, onRemove }: ChipRowProps) {
  return (
    <div className="-mx-4 mt-3 overflow-x-auto px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="flex gap-2">
        {chips.map((chip) => (
          <button
            key={`${chip.kind}:${chip.value}`}
            type="button"
            onClick={() => onRemove(chip)}
            className={cn(
              'group flex h-8 shrink-0 items-center gap-1.5 rounded-full pl-3 pr-2',
              'bg-[var(--color-brand-blue)]/12 ring-1 ring-[var(--color-brand-blue)]/30',
              'text-[13px] font-semibold text-[var(--color-brand-blue)]',
              'transition-all active:scale-[0.97]',
              'hover:bg-[var(--color-brand-blue)]/16',
            )}
          >
            <span className="truncate">{chip.label}</span>
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-brand-blue)]/15 transition-colors group-hover:bg-[var(--color-brand-blue)]/25">
              <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden>
                <path
                  d="M2.5 2.5l7 7M9.5 2.5l-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

interface ResultsMetaProps {
  isLoading: boolean;
  total?: number;
  isError: boolean;
}

function ResultsMeta({ isLoading, total, isError }: ResultsMetaProps) {
  return (
    <div className="mt-3 flex h-5 items-center text-[12px] font-semibold uppercase tracking-[0.06em] text-label-2">
      {isError ? (
        <span className="text-[#FF453A]">Ошибка загрузки</span>
      ) : isLoading && total == null ? (
        <span>Ищем…</span>
      ) : total != null ? (
        <span>
          {formatObjectsCount(total)} · по релевантности
        </span>
      ) : (
        <span>&nbsp;</span>
      )}
    </div>
  );
}

function formatObjectsCount(n: number): string {
  // Russian plural: 1 объект, 2-4 объекта, 5+ объектов
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} объект`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} объекта`;
  return `${n} объектов`;
}

// ---------------------------------------------------------------------------
// Suggestions — shown on empty input
// ---------------------------------------------------------------------------

const SUGGESTIONS: ReadonlyArray<{ label: string; query: string }> = [
  { label: 'Сочи 2-комн до 15М с видом на море', query: 'Сочи 2-комн до 15М с видом на море' },
  { label: 'Студия в Крыму до 8М', query: 'Студия в Крыму до 8М' },
  { label: 'Вилла в Адлере на первой линии', query: 'Вилла в Адлере на первой линии' },
  { label: 'Апартаменты Москва 5–10М', query: 'Апартаменты Москва 5–10М' },
];

function SuggestionsBlock({ onPick }: { onPick: (q: string) => void }) {
  return (
    <div className="px-4">
      <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-label-2">
        Попробуй
      </p>
      <div className="mt-3 flex flex-col gap-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s.query}
            type="button"
            onClick={() => onPick(s.query)}
            className={cn(
              'flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left',
              'bg-white/[0.04] ring-1 ring-white/8 transition-all',
              'hover:bg-white/[0.06] hover:ring-white/12 active:scale-[0.99]',
            )}
          >
            <span className="text-[20px]">✨</span>
            <span className="flex-1 text-[15px] font-medium text-label">
              {s.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyResultsBlock() {
  return (
    <div className="flex flex-col items-center px-6 pt-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/[0.06] ring-1 ring-white/10">
        <span className="text-[28px]">🔍</span>
      </div>
      <p className="mt-4 text-[18px] font-bold text-label">Ничего не нашли</p>
      <p className="mt-1.5 max-w-[280px] text-[14px] leading-relaxed text-label-2">
        Попробуй убрать какой-то фильтр или расширить бюджет.
      </p>
    </div>
  );
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center px-6 pt-16 text-center">
      <p className="text-[18px] font-bold text-label">Не удалось загрузить</p>
      <p className="mt-1.5 max-w-[280px] text-[14px] leading-relaxed text-label-2">
        {message}
      </p>
    </div>
  );
}

interface ResultsGridProps {
  items: Unit[];
}

function ResultsGrid({ items }: ResultsGridProps) {
  // Adapter throws if `unit.project` isn't embedded — the search API always
  // embeds it via `!inner` join, so this is safe.
  const properties = useMemo(
    () =>
      items
        .filter((u) => u.project) // defensive: skip if anything's off
        .map((u) => apiUnitToProperty(u)),
    [items],
  );

  return (
    <div className="grid grid-cols-2 gap-3 px-4">
      {properties.map((p) => (
        <PropertyCardCompact key={p.id} property={p} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error helper (mirror of /property/$id)
// ---------------------------------------------------------------------------

function messageFor(err: unknown): string {
  if (err instanceof ApiCallError) {
    if (err.status === 0 || err.code === 'network_error') {
      return 'Нет связи с сервером — проверь интернет.';
    }
    return err.message;
  }
  return 'Что-то пошло не так. Повтори через минуту.';
}
